import type { UserStatus } from "@seagames/contracts";
import { Icons } from "@seagames/ui";
import type { ReactNode } from "react";

/**
 * The designer's building blocks for the Users pages (her prototype's
 * `.rv`, `.st`, `.tag`, `.note`, `.rtools__f`), so the user, group and list
 * pages read as one family with her record detail and View Records.
 */

/** Her `.rv` section: white, a --line border, 10px radius; a 15px title on a hairline header. */
export function RvSection({
	title,
	aside,
	intro,
	children,
	id,
	flush = false,
	className = "",
}: {
	title: ReactNode;
	/** Right side of the header: her `.rv__edit` link-button, a count… */
	aside?: ReactNode;
	/** One short line under the header: what this section decides. */
	intro?: ReactNode;
	children: ReactNode;
	id?: string;
	/** No body padding (a table or a label/value list that pads itself). */
	flush?: boolean;
	className?: string;
}) {
	const headingId = id ? `${id}-heading` : undefined;
	return (
		<section aria-labelledby={headingId} className={`min-w-0 overflow-hidden rounded-box border border-gms-line bg-base-100 ${className}`}>
			<header className="flex min-h-[3.25rem] items-center gap-[0.625rem] border-b border-gms-line px-[1rem] py-[0.625rem]">
				<h2 id={headingId} className="min-w-0 flex-1 text-[0.9375rem] leading-snug font-semibold text-gms-navy-ink">
					{title}
				</h2>
				{aside && <div className="flex shrink-0 items-center gap-[0.375rem]">{aside}</div>}
			</header>
			{intro && <p className="max-w-[52rem] px-[1rem] pt-[0.75rem] text-[0.8125rem] leading-snug text-gms-ink-3">{intro}</p>}
			{flush ? children : <div className="px-[1rem] py-[0.875rem]">{children}</div>}
		</section>
	);
}

/** Her `.rv__edit`: a 32px navy text button with a small icon, for a section header. */
export function RvEditButton({ children, onClick, icon }: { children: ReactNode; onClick: () => void; icon?: ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="inline-flex h-[2rem] cursor-pointer items-center gap-[0.25rem] rounded-[0.5rem] px-[0.625rem] text-[0.8125rem] font-semibold text-gms-navy outline-none hover:bg-gms-navy-soft focus-visible:ring-2 focus-visible:ring-gms-navy/40 [&>svg]:size-[0.9375rem]"
		>
			{icon ?? <Icons.Pencil />}
			{children}
		</button>
	);
}

/** Her `.rv dl`: label / value rows split by hairlines. */
export function KvList({ children }: { children: ReactNode }) {
	return <dl className="px-[1rem] pt-[0.25rem] pb-[0.5rem]">{children}</dl>;
}

export function Kv({ label, children, title, valueClassName = "" }: { label: ReactNode; children: ReactNode; title?: string; valueClassName?: string }) {
	return (
		<div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-[0.75rem] border-t border-gms-line py-[0.5625rem] first:border-t-0">
			<dt className="text-[0.78125rem] text-gms-ink-3">{label}</dt>
			<dd className={`min-w-0 text-[0.84375rem] font-medium break-words text-gms-navy-ink ${valueClassName}`} title={title}>
				{children}
			</dd>
		</div>
	);
}

export const Muted = ({ children }: { children: ReactNode }) => <span className="font-normal text-gms-ink-3">{children}</span>;

const STATUS_TONE: Record<UserStatus, { chip: string; dot: string }> = {
	ACTIVE: { chip: "bg-gms-ok-soft text-gms-ok", dot: "bg-gms-ok" },
	INACTIVE: { chip: "bg-gms-soft-2 text-gms-ink-2", dot: "bg-gms-ink-3" },
	SUSPENDED: { chip: "bg-gms-bad-soft text-gms-bad", dot: "bg-gms-crit" },
};

/** Her status chip (`.st`): 22px, 4px radius, 11.5px semibold, a 7px dot. */
export function StatusChip({ status, children }: { status: UserStatus; children: ReactNode }) {
	const tone = STATUS_TONE[status];
	return <Chip tone={tone.chip} dot={tone.dot}>{children}</Chip>;
}

/** An active / inactive chip in the same shape (groups). */
export function ActiveChip({ active, children }: { active: boolean; children: ReactNode }) {
	const tone = active ? STATUS_TONE.ACTIVE : STATUS_TONE.INACTIVE;
	return <Chip tone={tone.chip} dot={tone.dot}>{children}</Chip>;
}

function Chip({ tone, dot, children }: { tone: string; dot: string; children: ReactNode }) {
	return (
		<span className={`inline-flex h-[1.375rem] items-center gap-[0.375rem] rounded-selector px-[0.5rem] text-[0.71875rem] leading-none font-semibold whitespace-nowrap ${tone}`}>
			<span aria-hidden className={`size-[0.4375rem] shrink-0 rounded-full ${dot}`} />
			{children}
		</span>
	);
}

const TAG_TONE = {
	navy: "bg-gms-navy-soft text-gms-navy",
	muted: "bg-gms-soft-2 text-gms-ink-2",
	ok: "bg-gms-ok-soft text-gms-ok",
	warn: "bg-gms-warn-soft text-gms-warn",
} as const;

/** Her `.tag` / `.pill`: 20px, 4px radius, 10.5px semibold. Roles, 2FA, "You"… */
export function Tag({ children, tone = "navy", title }: { children: ReactNode; tone?: keyof typeof TAG_TONE; title?: string }) {
	return (
		<span title={title} className={`inline-flex h-[1.25rem] max-w-full items-center rounded-selector px-[0.4375rem] text-[0.65625rem] font-semibold tracking-[0.02em] whitespace-nowrap ${TAG_TONE[tone]}`}>
			<span className="truncate">{children}</span>
		</span>
	);
}

/** Initials in a navy-soft circle (her `.av` on a light ground). */
export function Avatar({ name, size = "sm" }: { name: string; size?: "sm" | "lg" }) {
	return (
		<span
			aria-hidden
			className={`grid shrink-0 place-items-center rounded-full bg-gms-navy-soft font-semibold tracking-[0.02em] text-gms-navy ${
				size === "lg" ? "size-[3.5rem] text-[1.125rem]" : "size-[2rem] text-[0.71875rem]"
			}`}
		>
			{initials(name)}
		</span>
	);
}

export function initials(name: string) {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const a = parts[0]?.[0] ?? "?";
	const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : (parts[0]?.[1] ?? "");
	return `${a}${b}`.toUpperCase();
}

/** Her `.note` (navy-soft) / `.note--warn`: 12px 14px, 8px radius, 13px, an 18px icon. */
export function Note({ children, tone = "warn" }: { children: ReactNode; tone?: "info" | "warn" }) {
	return (
		<div role="note" className={`flex gap-[0.625rem] rounded-field px-[0.875rem] py-[0.75rem] text-[0.8125rem] leading-[1.45] text-gms-navy-ink ${tone === "warn" ? "bg-gms-warn-soft" : "bg-gms-navy-soft"}`}>
			<Icons.Alert className={`mt-[0.0625rem] size-[1.125rem] shrink-0 ${tone === "warn" ? "text-gms-warn" : "text-gms-navy"}`} />
			<div className="min-w-0">{children}</div>
		</div>
	);
}

/** Her `.rtools__f` "Filters" button: 46px, white, --line-2 border, ink label; the count in her red `.fchip__n`. */
export function FiltersButton({ label, count, onClick }: { label: string; count: number; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-haspopup="dialog"
			className="inline-flex h-[2.875rem] shrink-0 cursor-pointer items-center justify-center gap-[0.5rem] rounded-[0.5rem] border border-gms-line-2 bg-base-100 px-[0.875rem] text-[0.875rem] font-semibold text-gms-navy-ink outline-none transition-colors hover:border-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40"
		>
			<Icons.Filter className="size-[1.125rem] text-gms-ink-2" />
			{label}
			{count > 0 && (
				<span className="grid h-[1.125rem] min-w-[1.125rem] place-items-center rounded-full bg-gms-red px-[0.3125rem] text-[0.6875rem] font-bold text-white tabular-nums">{count}</span>
			)}
		</button>
	);
}
